import { Settings, Sliders, Link as LinkIcon, User } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/auth-store'
import { useToast } from '@/hooks/use-toast'
import { BasicSettings } from '@/components/device/basic-settings'
import { IntegrationConfig } from '@/components/device/integration-config'
import { useState } from 'react'

interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const { user } = useAuthStore()
  const { toast } = useToast()
  const [isUpdating, setIsUpdating] = useState(false)

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsUpdating(true)
    
    // Simulate API call
    setTimeout(() => {
      toast({
        title: 'Profil aktualisiert',
        description: 'Ihre Änderungen wurden erfolgreich gespeichert.',
      })
      setIsUpdating(false)
    }, 500)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Einstellungen
          </DialogTitle>
          <DialogDescription>
            Verwalten Sie Ihre Konto- und Anwendungseinstellungen
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="base" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="base">
              <Sliders className="h-4 w-4 mr-2" />
              Grundeinstellungen
            </TabsTrigger>
            <TabsTrigger value="integration">
              <LinkIcon className="h-4 w-4 mr-2" />
              Integration
            </TabsTrigger>
            <TabsTrigger value="profile">
              <User className="h-4 w-4 mr-2" />
              Benutzerprofil
            </TabsTrigger>
          </TabsList>

          {/* Base Settings Tab */}
          <TabsContent value="base" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Grundeinstellungen</CardTitle>
                <CardDescription>
                  Konfigurieren Sie grundlegende Einstellungen für Ihr Gerät
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BasicSettings />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integration Tab */}
          <TabsContent value="integration" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Integration & Verbindungen</CardTitle>
                <CardDescription>
                  Konfigurieren Sie Verbindungen zu externen Systemen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <IntegrationConfig />
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Profile Tab */}
          <TabsContent value="profile" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Benutzerprofil</CardTitle>
                <CardDescription>
                  Verwalten Sie Ihre persönlichen Informationen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstname">Vorname</Label>
                      <Input
                        id="firstname"
                        defaultValue={user?.firstname || ''}
                        placeholder="Vorname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastname">Nachname</Label>
                      <Input
                        id="lastname"
                        defaultValue={user?.lastname || ''}
                        placeholder="Nachname"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      defaultValue={user?.email || ''}
                      placeholder="email@example.com"
                      disabled
                    />
                    <p className="text-xs text-gray-500">
                      E-Mail-Adresse kann nicht geändert werden
                    </p>
                  </div>

                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      disabled={isUpdating}
                      className="bg-brand-primary hover:bg-brand-primary/90"
                    >
                      {isUpdating ? 'Speichert...' : 'Profil aktualisieren'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
